import { useEffect, useState, useCallback, createContext, useContext } from "react";
import { AuthContext, useAuth, type Admin, type AuthContextType } from "@/lib/auth-context";
import { Switch, Route, useLocation, Redirect } from "wouter";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/lib/theme-provider";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { AppHeader } from "@/components/app-header";
import { ImpersonationBanner } from "@/components/impersonation-banner";
import { OrgSettingsProvider } from "@/lib/org-settings-context";

// Demo Data Context
interface DemoDataContextType {
  isDemoDataEnabled: boolean;
  toggleDemoData: () => void;
}

const DemoDataContext = createContext<DemoDataContextType | null>(null);

export function useDemoData() {
  const context = useContext(DemoDataContext);
  if (!context) {
    throw new Error("useDemoData must be used within a DemoDataProvider");
  }
  return context;
}

function DemoDataProvider({ children }: { children: React.ReactNode }) {
  const [isDemoDataEnabled, setIsDemoDataEnabled] = useState(() => {
    const stored = localStorage.getItem("cardvault-demo-data");
    return stored !== null ? stored === "true" : true; // Default to true
  });

  const toggleDemoData = useCallback(() => {
    setIsDemoDataEnabled((prev) => {
      const newValue = !prev;
      localStorage.setItem("cardvault-demo-data", String(newValue));
      // Refresh all queries when demo data is toggled
      queryClient.invalidateQueries();
      return newValue;
    });
  }, []);

  return (
    <DemoDataContext.Provider value={{ isDemoDataEnabled, toggleDemoData }}>
      {children}
    </DemoDataContext.Provider>
  );
}

// Feature Flags - extracted to break circular dependency with app-sidebar
import { FeatureFlagsProvider, useFeatureFlags } from "@/lib/feature-flags-context";
export { useFeatureFlags } from "@/lib/feature-flags-context";

// Pages
import Login from "@/pages/login";
import Dashboard from "@/pages/dashboard";

// Consolidated Pages (new)
import Settings from "@/pages/settings";
import Integrations from "@/pages/integrations";
import Inventory from "@/pages/inventory";

// Entity Pages (keeping detail pages, will consolidate lists later)
import OrganizationCreate from "@/pages/organization/create";
import OrganizationView from "@/pages/organization/view";
import OrganizationDetail from "@/pages/organization/detail";
import BusinessUnitCreate from "@/pages/business-unit/create";
import BusinessUnitDetail from "@/pages/business-unit/detail";
import BusinessUnitDrafted from "@/pages/business-unit/drafted";
import BusinessUnitPending from "@/pages/business-unit/pending";
import ProductCreate from "@/pages/product/create";
import ProductView from "@/pages/product/view";
import ProductDrafted from "@/pages/product/drafted";
import CustomerCreate from "@/pages/customer/create";
import CustomerView from "@/pages/customer/view";
import CustomerDrafted from "@/pages/customer/drafted";
import CustomerCards from "@/pages/customer/cards";
import WalletView from "@/pages/wallet/view";

// Operations
import ReportCenter from "@/pages/report-center";
import Transactions from "@/pages/transactions";
import CustomerSupport from "@/pages/customer-support";
import Docs from "@/pages/docs";
import Changelog from "@/pages/changelog";
import PrivacyPolicy from "@/pages/privacy-policy";
import PlatformAnalytics from "@/pages/platform-analytics";
import OrgAnalytics from "@/pages/org-analytics";
import AuditLog from "@/pages/audit-log";
import EmailTemplates from "@/pages/email-templates";

// Developer
import ApiKeys from "@/pages/developer/api-keys";
import DeveloperWebhooks from "@/pages/developer/webhooks";
import UsageDashboard from "@/pages/developer/usage";
import SecurityTests from "@/pages/developer/security-tests";
import PartnerApiDocs from "@/pages/developer/docs";

// Customer Portal
import PortalLogin from "@/pages/portal/login";
import PortalHome from "@/pages/portal/home";
import PortalWallet from "@/pages/portal/wallet";
import PortalCards from "@/pages/portal/cards";
import PortalCardDetail from "@/pages/portal/card-detail";
import PortalTransactions from "@/pages/portal/transactions";
import PortalAccount from "@/pages/portal/account";
import PortalHelp from "@/pages/portal/help";
import { CustomerProvider, useCustomer } from "@/lib/customer-context";
import { CustomerLayout } from "@/components/customer/customer-layout";
import { SetPinModal } from "@/components/customer/set-pin-modal";

function ProtectedPortalRoute({ children }: { children: React.ReactNode }) {
  const { customer, isLoading } = useCustomer();
  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }
  if (!customer) return <Redirect to="/portal/login" />;
  return (
    <CustomerLayout>
      {children}
      {/* Forced first-time PIN setup — cannot be dismissed */}
      <SetPinModal open={!!customer.pinSetupRequired} required />
    </CustomerLayout>
  );
}

// Auth context (exported from @/lib/auth-context to avoid circular dependency)
// Re-export useAuth for backwards compatibility
export { useAuth } from "@/lib/auth-context";

function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<Admin | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [, setLocation] = useLocation();

  const refreshUser = async () => {
    try {
      const response = await apiRequest("GET", "/api/auth/me");
      if (response.ok) {
        const data = await response.json();
        setUser(data.admin);
      } else {
        setUser(null);
      }
    } catch (error) {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refreshUser();
  }, []);

  const login = async (email: string, password: string) => {
    const response = await apiRequest("POST", "/api/auth/login", { email, password });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || "Login failed");
    }
    const data = await response.json();
    setUser(data.admin);
    setLocation("/");
  };

  const logout = async () => {
    try {
      await apiRequest("POST", "/api/auth/logout");
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    queryClient.clear();
    setLocation("/login");
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return <Redirect to="/login" />;
  }

  return <>{children}</>;
}

type AdminRole = "SUPER" | "ORGANIZATION" | "BUSINESS_UNIT" | "FINANCE";

function RoleRoute({ children, roles }: { children: React.ReactNode; roles: AdminRole[] }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return <Redirect to="/login" />;
  }

  if (!roles.includes(user.role)) {
    return <Redirect to="/" />;
  }

  return <>{children}</>;
}

function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  return <RoleRoute roles={["SUPER"]}>{children}</RoleRoute>;
}

function AppLayout({ children }: { children: React.ReactNode }) {
  return (
    <TooltipProvider>
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset className="flex flex-col">
          <AppHeader />
          <ImpersonationBanner />
          <main className="flex-1 overflow-auto">
            {children}
          </main>
        </SidebarInset>
      </SidebarProvider>
    </TooltipProvider>
  );
}

function AppRoutes() {
  return (
    <Switch>
      <Route path="/login">
        <Login />
      </Route>

      <Route path="/">
        <ProtectedRoute>
          <AppLayout>
            <Dashboard />
          </AppLayout>
        </ProtectedRoute>
      </Route>

      {/* Organization Routes — SUPER only */}
      <Route path="/organization/create">
        <RoleRoute roles={["SUPER"]}>
          <AppLayout>
            <OrganizationCreate />
          </AppLayout>
        </RoleRoute>
      </Route>
      <Route path="/organization/view">
        <RoleRoute roles={["SUPER"]}>
          <AppLayout>
            <OrganizationView />
          </AppLayout>
        </RoleRoute>
      </Route>
      <Route path="/organization/:id">
        <RoleRoute roles={["SUPER"]}>
          <AppLayout>
            <OrganizationDetail />
          </AppLayout>
        </RoleRoute>
      </Route>

      {/* Business Unit Routes — SUPER + ORGANIZATION */}
      <Route path="/business-unit/create">
        <RoleRoute roles={["SUPER", "ORGANIZATION"]}>
          <AppLayout>
            <BusinessUnitCreate />
          </AppLayout>
        </RoleRoute>
      </Route>
      <Route path="/business-unit/view">
        <Redirect to="/organizations" />
      </Route>
      <Route path="/business-unit/drafted">
        <RoleRoute roles={["SUPER", "ORGANIZATION"]}>
          <AppLayout>
            <BusinessUnitDrafted />
          </AppLayout>
        </RoleRoute>
      </Route>
      <Route path="/business-unit/pending">
        <RoleRoute roles={["SUPER", "ORGANIZATION"]}>
          <AppLayout>
            <BusinessUnitPending />
          </AppLayout>
        </RoleRoute>
      </Route>
      <Route path="/business-unit/:id">
        <RoleRoute roles={["SUPER", "ORGANIZATION"]}>
          <AppLayout>
            <BusinessUnitDetail />
          </AppLayout>
        </RoleRoute>
      </Route>

      {/* Product Routes — SUPER + ORGANIZATION */}
      <Route path="/product/create">
        <RoleRoute roles={["SUPER", "ORGANIZATION"]}>
          <AppLayout>
            <ProductCreate />
          </AppLayout>
        </RoleRoute>
      </Route>
      <Route path="/product/view">
        <RoleRoute roles={["SUPER", "ORGANIZATION"]}>
          <AppLayout>
            <ProductView />
          </AppLayout>
        </RoleRoute>
      </Route>
      <Route path="/product/drafted">
        <RoleRoute roles={["SUPER", "ORGANIZATION"]}>
          <AppLayout>
            <ProductDrafted />
          </AppLayout>
        </RoleRoute>
      </Route>
      <Route path="/product/:id">
        {({ id }) => <Redirect to={`/products?select=${id}`} />}
      </Route>

      {/* Consolidated Inventory Page — SUPER + ORGANIZATION */}
      <Route path="/inventory">
        <RoleRoute roles={["SUPER", "ORGANIZATION"]}>
          <AppLayout>
            <Inventory />
          </AppLayout>
        </RoleRoute>
      </Route>

      {/* Legacy Inventory Redirects */}
      <Route path="/inventory/buy-cards">
        <Redirect to="/inventory?tab=orders" />
      </Route>
      <Route path="/inventory/shipping-report">
        <Redirect to="/inventory?tab=shipping" />
      </Route>
      {/* Customer Routes — SUPER + ORGANIZATION + BUSINESS_UNIT */}
      <Route path="/customer/create">
        <RoleRoute roles={["SUPER", "ORGANIZATION", "BUSINESS_UNIT"]}>
          <AppLayout>
            <CustomerCreate />
          </AppLayout>
        </RoleRoute>
      </Route>
      <Route path="/customer/view">
        <RoleRoute roles={["SUPER", "ORGANIZATION", "BUSINESS_UNIT"]}>
          <AppLayout>
            <CustomerView />
          </AppLayout>
        </RoleRoute>
      </Route>
      <Route path="/customer/drafted">
        <RoleRoute roles={["SUPER", "ORGANIZATION", "BUSINESS_UNIT"]}>
          <AppLayout>
            <CustomerDrafted />
          </AppLayout>
        </RoleRoute>
      </Route>
      {/* Legacy customer/cards routes - redirect to /cards */}
      <Route path="/customer/cards">
        <Redirect to="/cards" />
      </Route>
      <Route path="/customer/cards/:id">
        {({ id }) => <Redirect to={`/cards?select=${id}`} />}
      </Route>
      <Route path="/customer/:id">
        {({ id }) => <Redirect to={`/customers?select=${id}`} />}
      </Route>

      {/* Cards Routes (promoted to top-level) — SUPER + ORGANIZATION + BUSINESS_UNIT */}
      <Route path="/cards">
        <RoleRoute roles={["SUPER", "ORGANIZATION", "BUSINESS_UNIT"]}>
          <AppLayout>
            <CustomerCards />
          </AppLayout>
        </RoleRoute>
      </Route>
      <Route path="/cards/:id">
        {({ id }) => <Redirect to={`/cards?select=${id}`} />}
      </Route>

      {/* Wallet Routes — SUPER + ORGANIZATION + BUSINESS_UNIT */}
      <Route path="/wallets">
        <RoleRoute roles={["SUPER", "ORGANIZATION", "BUSINESS_UNIT"]}>
          <AppLayout>
            <WalletView />
          </AppLayout>
        </RoleRoute>
      </Route>
      <Route path="/wallets/:id">
        {({ id }) => <Redirect to={`/wallets?select=${id}`} />}
      </Route>

      {/* New Plural URL Aliases (point to existing pages for now) */}
      <Route path="/organizations">
        <RoleRoute roles={["SUPER"]}>
          <AppLayout>
            <OrganizationView />
          </AppLayout>
        </RoleRoute>
      </Route>
      <Route path="/organizations/:id">
        <RoleRoute roles={["SUPER"]}>
          <AppLayout>
            <OrganizationDetail />
          </AppLayout>
        </RoleRoute>
      </Route>
      <Route path="/business-units">
        <Redirect to="/organizations" />
      </Route>
      <Route path="/business-units/:id">
        <RoleRoute roles={["SUPER", "ORGANIZATION"]}>
          <AppLayout>
            <BusinessUnitDetail />
          </AppLayout>
        </RoleRoute>
      </Route>
      <Route path="/products">
        <RoleRoute roles={["SUPER", "ORGANIZATION"]}>
          <AppLayout>
            <ProductView />
          </AppLayout>
        </RoleRoute>
      </Route>
      <Route path="/products/:id">
        {({ id }) => <Redirect to={`/products?select=${id}`} />}
      </Route>
      <Route path="/customers">
        <RoleRoute roles={["SUPER", "ORGANIZATION", "BUSINESS_UNIT"]}>
          <AppLayout>
            <CustomerView />
          </AppLayout>
        </RoleRoute>
      </Route>
      <Route path="/customers/:id">
        {({ id }) => <Redirect to={`/customers?select=${id}`} />}
      </Route>

      {/* Operations Routes */}
      {/* Pay User consolidated into Customer Wallet tab - redirect to customers */}
      <Route path="/pay-user">
        <Redirect to="/customers" />
      </Route>
      <Route path="/reports">
        <ProtectedRoute>
          <AppLayout>
            <ReportCenter />
          </AppLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/report-center">
        <Redirect to="/reports" />
      </Route>
      <Route path="/analytics">
        <RoleRoute roles={["SUPER", "ORGANIZATION"]}>
          <AppLayout>
            <OrgAnalytics />
          </AppLayout>
        </RoleRoute>
      </Route>
      <Route path="/transactions">
        <ProtectedRoute>
          <AppLayout>
            <Transactions />
          </AppLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/support">
        <RoleRoute roles={["SUPER", "ORGANIZATION", "BUSINESS_UNIT"]}>
          <AppLayout>
            <CustomerSupport />
          </AppLayout>
        </RoleRoute>
      </Route>
      <Route path="/customer-support">
        <Redirect to="/support" />
      </Route>

      {/* Consolidated Settings Page */}
      <Route path="/settings">
        <ProtectedRoute>
          <AppLayout>
            <Settings />
          </AppLayout>
        </ProtectedRoute>
      </Route>

      {/* Integrations Page — SUPER only */}
      <Route path="/integrations">
        <RoleRoute roles={["SUPER"]}>
          <AppLayout>
            <Integrations />
          </AppLayout>
        </RoleRoute>
      </Route>

      {/* Legacy Settings Redirects */}
      <Route path="/settings/profile">
        <Redirect to="/settings?tab=profile" />
      </Route>
      <Route path="/settings/team">
        <Redirect to="/settings?tab=team" />
      </Route>
      <Route path="/settings/integrations">
        <Redirect to="/integrations" />
      </Route>

      {/* Documentation — SUPER + ORGANIZATION (tabs filtered by role) */}
      <Route path="/docs">
        <RoleRoute roles={["SUPER", "ORGANIZATION"]}>
          <AppLayout>
            <Docs />
          </AppLayout>
        </RoleRoute>
      </Route>
      <Route path="/changelog">
        <SuperAdminRoute>
          <AppLayout>
            <Changelog />
          </AppLayout>
        </SuperAdminRoute>
      </Route>
      <Route path="/privacy-policy">
        <PrivacyPolicy />
      </Route>

      {/* Platform Analytics (SUPER only) */}
      <Route path="/platform-analytics">
        <SuperAdminRoute>
          <AppLayout>
            <PlatformAnalytics />
          </AppLayout>
        </SuperAdminRoute>
      </Route>

      {/* Audit Log (SUPER only) */}
      <Route path="/audit-log">
        <SuperAdminRoute>
          <AppLayout>
            <AuditLog />
          </AppLayout>
        </SuperAdminRoute>
      </Route>

      {/* Email Templates (SUPER only) */}
      <Route path="/email-templates">
        <SuperAdminRoute>
          <AppLayout>
            <EmailTemplates />
          </AppLayout>
        </SuperAdminRoute>
      </Route>

      {/* Developer Routes — SUPER only */}
      <Route path="/developer/api-keys">
        <SuperAdminRoute>
          <AppLayout>
            <ApiKeys />
          </AppLayout>
        </SuperAdminRoute>
      </Route>
      <Route path="/developer/webhooks">
        <SuperAdminRoute>
          <AppLayout>
            <DeveloperWebhooks />
          </AppLayout>
        </SuperAdminRoute>
      </Route>
      <Route path="/developer/usage">
        <SuperAdminRoute>
          <AppLayout>
            <UsageDashboard />
          </AppLayout>
        </SuperAdminRoute>
      </Route>
      <Route path="/developer/security-tests">
        <SuperAdminRoute>
          <AppLayout>
            <SecurityTests />
          </AppLayout>
        </SuperAdminRoute>
      </Route>
      <Route path="/developer/docs">
        <SuperAdminRoute>
          <AppLayout>
            <PartnerApiDocs />
          </AppLayout>
        </SuperAdminRoute>
      </Route>

      {/* Customer Portal Routes */}
      <Route path="/portal/login">
        <PortalLogin />
      </Route>

      {/* Customer Portal Routes */}
      <Route path="/portal">
        <Redirect to="/portal/home" />
      </Route>

      <Route path="/portal/dashboard">
        <Redirect to="/portal/home" />
      </Route>

      <Route path="/portal/home">
        <ProtectedPortalRoute>
          <PortalHome />
        </ProtectedPortalRoute>
      </Route>

      <Route path="/portal/wallet">
        <ProtectedPortalRoute>
          <PortalWallet />
        </ProtectedPortalRoute>
      </Route>

      <Route path="/portal/cards">
        <ProtectedPortalRoute>
          <PortalCards />
        </ProtectedPortalRoute>
      </Route>

      <Route path="/portal/cards/:id">
        <ProtectedPortalRoute>
          <PortalCardDetail />
        </ProtectedPortalRoute>
      </Route>

      <Route path="/portal/transactions">
        <ProtectedPortalRoute>
          <PortalTransactions />
        </ProtectedPortalRoute>
      </Route>

      <Route path="/portal/account">
        <ProtectedPortalRoute>
          <PortalAccount />
        </ProtectedPortalRoute>
      </Route>

      <Route path="/portal/help">
        <ProtectedPortalRoute>
          <PortalHelp />
        </ProtectedPortalRoute>
      </Route>

      {/* Catch all - 404 */}
      <Route>
        <ProtectedRoute>
          <AppLayout>
            <div className="flex flex-col items-center justify-center h-[calc(100vh-4rem)]">
              <h1 className="text-4xl font-bold">404</h1>
              <p className="text-muted-foreground mt-2">Page not found</p>
            </div>
          </AppLayout>
        </ProtectedRoute>
      </Route>
    </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="light" storageKey="cardvault-theme">
        <FeatureFlagsProvider>
          <DemoDataProvider>
            <AuthProvider>
              <OrgSettingsProvider>
                <CustomerProvider>
                  <AppRoutes />
                  <Toaster />
                </CustomerProvider>
              </OrgSettingsProvider>
            </AuthProvider>
          </DemoDataProvider>
        </FeatureFlagsProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
